import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Lock, Mail, ShieldCheck, ArrowLeft, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';

import { useAuth } from '@/contexts/AuthContext';
import { useMembership } from '@/contexts/MembershipContext';
import {
  resolveApprovedLoginContinuation,
} from '@/governor/routing/RouteAccessAuthority';
import { useCommandCenterAuthority } from '@/hooks/useCommandCenterAuthority';

/**
 * Standalone /login fallback page.
 *
 * Most surfaces (Header, Join, InviteLanding) open the existing AuthModal
 * directly via `openAuthModal('signin')`. This page exists so:
 *   1. Users who land on /login by URL, bookmark, or external link have a
 *      proper, mobile-friendly sign-in experience.
 *   2. The Sign In affordance is never a dead link — every entry point has
 *      a real fallback.
 *
 * Behavior:
 *   - Email + password fields with native validation.
 *   - "Forgot password?" → triggers the existing reset-password flow.
 *   - "" link → routes to /join (the public onboarding
 *     surface) where the existing signup AuthModal can be opened.
 *
 * Post-login redirect:
 *   - Already-paid (or staff/admin) → "/" (dashboard / family tree)
 *   - Authenticated but NOT paid → "/" and the trial-checkout modal pops
 *     automatically (via the existing kc:open-trial-checkout event the
 *     MembershipProvider already listens for).
 *
 * NOTE: This page does NOT touch the auth system, Stripe, invite logic,
 * homepage content, Join layout, or the database — it is a thin form on
 * top of the EXISTING signIn / sendPasswordReset functions.
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, sendPasswordReset, isAuthenticated, isPaid, isStaff, loading: authLoading } = useAuth();

  const commandCenter =
    useCommandCenterAuthority(
      isAuthenticated,
    );
  const { openMembershipModal } = useMembership();

  // Login continuation is normalized against the governed internal
  // route allowlist. Unknown, public, obsolete, or unauthorized paths
  // fall back to the canonical member application.
  const nextPath = useMemo(() => {
    const params =
      new URLSearchParams(location.search);

    return resolveApprovedLoginContinuation(
      params.get('next'),
      isStaff,
      commandCenter.allowed,
    );
  }, [
    location.search,
    isStaff,
    commandCenter.allowed,
  ]);

  const [view, setView] = useState<'signin' | 'forgot'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    document.title =
      view === 'forgot'
        ? 'Reset Password | KinsCircle'
        : 'Sign In | KinsCircle';
  }, [view]);

  // If already signed in, bounce to the right destination.
  useEffect(() => {
    if (
      authLoading ||
      commandCenter.loading
    ) return;
    if (!isAuthenticated) return;
    if (nextPath !== '/app') {
      navigate(nextPath, { replace: true });
      return;
    }
    if (isPaid || isStaff) {
      navigate('/app', { replace: true });
    } else {
      // Free / unsubscribed account → land on home and pop the trial card.
      navigate('/app', { replace: true });
      window.setTimeout(() => {
        try {
          // member login must never open signup/payment modal
        } catch {
          /* noop */
        }
      }, 250);
    }
  }, [
    authLoading,
    commandCenter.loading,
    isAuthenticated,
    isPaid,
    isStaff,
    nextPath,
    navigate,
  ]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.');
      return;
    }
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const result = await signIn(email.trim(), password);
      if (!result.ok) {
        setError(result.error || 'Incorrect email or password. Please try again.');
        setLoading(false);
        return;
      }
      toast.success('Welcome back!');
      setLoading(false);
      window.location.href = '/app';
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setError(null);
    setInfo(null);
    setLoading(true);
    const result = await sendPasswordReset(email.trim());
    setLoading(false);
    if (!result.ok) {
      setError(result.error || 'Could not send reset email. Please try again.');
      return;
    }
    setInfo(`Password reset email sent to ${email.trim()}. Check your inbox.`);
    toast.success('Password reset email sent');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 flex flex-col">
      {/* Header */}
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="/brand/kinscircle-tree-icon.png"
            alt="KinsCircle"
            className="w-10 h-10 rounded-xl object-cover shadow"
          />
          <div className="font-serif text-xl font-bold text-slate-900">
            Kins<span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">Circle</span>
          </div>
        </Link>
        
      </header>

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white shadow-xl border border-fuchsia-100 overflow-hidden">
            {/* Card header */}
            <div className="bg-gradient-to-br from-fuchsia-700 via-fuchsia-600 to-pink-600 text-white p-6">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-white/30">
                  <img src="/brand/kinscircle-tree-icon.png" alt="KinsCircle" className="w-full h-full object-cover rounded-full" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl font-bold leading-tight">
                    {view === 'forgot' ? 'Reset your password' : 'Welcome back'}
                  </h1>
                  <p className="text-white/80 text-xs mt-0.5">
                    {view === 'forgot'
                      ? "Enter your email and we'll send you a reset link."
                      : 'Sign in to your KinsCircle family.'}
                  </p>
                </div>
              </div>
            </div>

            {view === 'signin' ? (
              <form onSubmit={handleSignIn} className="p-6 space-y-4" noValidate>
                <div>
                  <Label htmlFor="login-email" className="text-slate-800">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@family.com"
                      className="pl-9"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-slate-800">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setView('forgot'); setError(null); setInfo(null); }}
                      className="text-xs font-semibold text-fuchsia-700 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      className="pl-9"
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">
                    {error}
                  </div>
                )}
                {info && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{info}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                    <span className="bg-white px-2 text-slate-400">or</span>
                  </div>
                </div>

                

                <p className="text-[11px] text-slate-500 inline-flex items-center justify-center gap-1.5 w-full">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Private. Secure. Family-only access.
                </p>
              </form>
            ) : (
              <form onSubmit={handleForgot} className="p-6 space-y-4">
                <div>
                  <Label htmlFor="forgot-email-page" className="text-slate-800">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="forgot-email-page"
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@family.com"
                      className="pl-9"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">
                    {error}
                  </div>
                )}
                {info && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{info}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setView('signin'); setError(null); setInfo(null); }}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-fuchsia-700 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </button>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-[11px] text-slate-500">
            By continuing you agree to our{' '}
            <Link to="/terms" className="underline hover:text-slate-700">Terms</Link> and{' '}
            <Link to="/privacy" className="underline hover:text-slate-700">Privacy</Link>.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
